import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import Routes from './Routes';
import LambdaFetch from './functions/FetchFromLambda';
import AppBar from './components/AppBar';
import { Button } from '@material-ui/core';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useAuth } from './hooks/useAuth';
import { useDynamicTitle } from './hooks/useDynamicTitle';

import './App.css';
import LoginPage from './LoginPage';

function AppCognito() {
  const { enqueueSnackbar } = useSnackbar();
  
  const [state, setState] = useState({
    isAuth: false,
    doneTokenCheck: false,
    isGettingProfile: true,
    isInvalidUser: false,
    user: {},
  });
  const [clientModules,setClientModules] = useState(null)
  const [federatedIdName, setFederatedIdName] = useState('Google');
  const [token, setToken] = useState(null);
  const [awsRegion, setAwsRegion] = useState(null);
  const [appVals, setAppVals] = useState({
    appWidth: 1000,
    primaryAppColor: '#1482C5',
    trinaryAppColor: '#0d6eaa',
  });
  const [logoVals, setLogoVals] = useState({
    logoUrl: '12',
    logoHeight: '40px',
    logoHeightSmall: '30px',
    logoBlackUrl: null
  });

  // Initialize createSnack function
  const createSnack = useCallback((message, type, duration) => {
    enqueueSnackbar(message, {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
      variant: type,
      autoHideDuration: duration,
    });
  }, [enqueueSnackbar]);

  // Initialize useAuth hook
  const { signOut: authSignOut, isSigningOut } = useAuth({ 
    createSnack 
  });

  // New function to initialize Cognito with environment variables
  async function initializeCognito() {
    const awsRegion = process.env.REACT_APP_AWS_REGION;
    const cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
    const cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    
    // Dynamically set redirect URL based on environment
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const redirectURL = isLocalhost 
      ? `${window.location.protocol}//${window.location.host}`
      : (process.env.REACT_APP_REDIRECT_URL || window.location.origin);
    
    // Set AWS region for later use
    setAwsRegion(awsRegion);
    
    // Set logo values from environment variables if available
    if (process.env.REACT_APP_LOGO_URL) {
      setLogoVals({
        logoUrl: process.env.REACT_APP_LOGO_URL,
        logoHeight: process.env.REACT_APP_LOGO_HEIGHT || '40px',
        logoHeightSmall: process.env.REACT_APP_LOGO_HEIGHT_SMALL || '30px',
        logoBlackUrl: process.env.REACT_APP_LOGO_BLACK_URL || null,
        logoBlackHeight: process.env.REACT_APP_LOGO_BLACK_HEIGHT || null
      });
    }

    // Configure Amplify
    Amplify.configure({
      Auth: {
        region: awsRegion,
        userPoolId: cognitoUserPoolId,
        userPoolWebClientId: cognitoClientId,
        oauth: {
          domain: cognitoDomain,
          scope: ['email', 'openid'],
          redirectSignIn: redirectURL,
          redirectSignOut: redirectURL,
          responseType: 'code',
        },
      },
    });

    // Set federated ID name - use environment variable or default to 'Google'
    const federatedId = process.env.REACT_APP_FEDERATED_ID_NAME;
    setFederatedIdName(federatedId);
    
  }
  // Initialize dynamic title (Stage - Apix)
  useDynamicTitle();


  useEffect(() => {
    const fetchData = async () => {
      // Initialize Cognito configuration first using environment variables
      await initializeCognito();
      
      // Set up event listeners
      window.addEventListener('resize', () => calcSize());

      Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
          case 'signIn':
            auditUser(
              data.signInUserSession.idToken.payload.email,
              data.signInUserSession.idToken.jwtToken
            );
            break;
          case 'cognitoHostedUI':
            setToken('grating...');
            getToken().then((userToken) =>
              setToken(userToken.idToken.jwtToken)
            );
            break;
          case 'signOut':
            setToken(null);
            break;
          case 'signIn_failure':
          case 'cognitoHostedUI_failure':
            console.log('Sign in failure', data);
            break;
          default:
            break;
        }
      });

      // Check authentication
      await authUser();
    };
    fetchData();
  }, []);

  const calcSize = () => {
    setAppVals({ ...appVals, appWidth: window.innerWidth });
  };

  async function auditUser(user, token) {
    await LambdaFetch(
      'audit',
      'post',
      token,
      JSON.stringify({
        action: 'write',
        type: 'login',
      }),
      '',
      { login }
    );
  }
  
  async function authUser() {
    const ses = await Auth.currentSession()
      .then((session) => session)
      .catch(() => {
        setState({ ...state, doneTokenCheck: true });
        console.log('Not signed in');
      });

    if (!ses) return null;
    if (!ses.idToken) return null;

    const curJwt = ses.idToken.jwtToken;

    if (!curJwt) {
      return null;
    }

    const currentSessionUser = await getUser();
    const { email } = currentSessionUser.signInUserSession.idToken.payload;

    // After successful authentication, run clientInfo
    await clientInfo();
    getUserInfo(email, curJwt);
  }

  async function getUserInfo(email, jwt) {
    try {
    const userToken = await LambdaFetch(
      'login',
      'post',
      null,
      JSON.stringify({
        action: 'cognito-login',
        accessToken: jwt,
        user: email,
        isLoggingIn: true,
      }),
      '',
      { login }
    );

    const pagePermission = userToken.data.roleActions.reduce((acc, cur) => {
      if (cur.type === 'page') {
        if (acc[cur.filter1]) {
          acc[cur.filter1] = acc[cur.filter1] ? true : cur.active;
        } else {
          acc[cur.filter1] = cur.active === 1 ? true : false;
        }
      }
      return acc;
    }, {});

    const subPagePermission = userToken.data.roleActions.reduce((acc, cur) => {
      if (cur.type === 'subpage') {
        let prop = `${cur.filter1}/${cur.filter2}`
        if (acc[prop]) {
          acc[prop] = acc[prop] ? true : cur.active;
        } else {
          acc[prop] = cur.active === 1 ? true : false;
        }
      }
      return acc;
    }, {});

    setState({
      ...state,
      doneTokenCheck: true,
      isAuth: true,
      user: {
        ...userToken.data.userInfo,
        accessToken: jwt,
        pagePermission: pagePermission,
        subPagePermission: subPagePermission,
        roleActions: userToken.data.roleActions,
      },
    });
  } catch(e) {
    console.log(e)
    setState({
      ...state,
      doneTokenCheck: true,
      isAuth: false,
      user: {
        accessToken: jwt,
        pagePermission: {},
        subPagePermission: {},
        roleActions: [],
      },
    });
  }
  }

  async function clientInfo() {
    const resp = await LambdaFetch(
      'login',
      'post',
      null,
      JSON.stringify({
        action: 'get-client-values',
      }),
      '',
      { login }
    );
    
    // Extract non-sensitive values like logos, colors, etc.
    const logoUrl = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-url'
    )?.R_VALUE;
    const logoHeight = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-height'
    )?.R_VALUE;
    const logoHeightSmall = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-height-small'
    )?.R_VALUE;
    const logoBlackUrl = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-black'
    )?.R_VALUE;
    const logoBlackHeight = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-height-black'
    )?.R_VALUE;
    const apixModules = resp.data.clientValues.find(
      (row) => row.R_KEY === 'apix-modules'
    )?.R_VALUE;
    
    const clientModules = apixModules ? apixModules : -1

    const colorScheme = resp.data.clientValues.reduce((acc, cur) => {
      if (cur.R_KEY === 'primary_color' && cur.R_ACTIVE === 1) {
        acc['primaryColor'] = cur.R_VALUE;
      }
      if (cur.R_KEY === 'trinary_color' && cur.R_ACTIVE === 1) {
        acc['trinaryColor'] = cur.R_VALUE;
      }
      return acc;
    }, {});

    setClientModules(clientModules)
    setAppColor(colorScheme.primaryColor, colorScheme.trinaryColor);

    // Update logo values if database has different values (optional)
    if (logoUrl && logoUrl !== process.env.REACT_APP_LOGO_URL) {
      setLogoVals(prev => ({ ...prev, logoUrl, logoHeight, logoHeightSmall, logoBlackUrl, logoBlackHeight }));
    }
    
    return null;
  }
  
  const setAppColor = (primaryColor, trinaryColor) => {
    const root = document.documentElement;
    const primary = primaryColor ? primaryColor : '#1482C5';
    const trinary = trinaryColor ? trinaryColor : '#0d6eaa';
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--trinary-color', trinary);
    setState({ ...state, primaryAppColor: primary, trinaryAppColor: trinary });
  };

  function getToken() {
    return Auth.currentSession()
      .then((session) => session)
      .catch((err) => console.log(err));
  }

  async function getUser() {
    return Auth.currentAuthenticatedUser()
      .then((currentUser) => currentUser)
      .catch(() => console.log('Not signed in'));
  }

  // Handle sign out initiated from another tab
  const handleExternalSignOut = useCallback(async () => {
    try { await Auth.signOut({ global: true}); } catch (e) {}
    setState({
      isAuth: false,
      doneTokenCheck: true,
      isGettingProfile: false,
      isInvalidUser: false,
      user: {},
    });
    setToken(null);
    createSnack('Signed out in another tab', 'info', 3000);
  }, [createSnack]);

  // Listen for cross-tab sign-out events (BroadcastChannel + storage fallback)
  useEffect(() => {
    if (!state.isAuth) return;

    let bc;
    const onStorage = (e) => {
      if (e.key === 'apix:signout') {
        handleExternalSignOut();
      }
    };

    try {
      if ('BroadcastChannel' in window) {
        bc = new BroadcastChannel('apix-auth');
        bc.onmessage = (event) => {
          if (event?.data?.type === 'SIGN_OUT') {
            handleExternalSignOut();
          }
        };
      }
    } catch (_) {}

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, [state.isAuth, handleExternalSignOut]);

  async function signOut() {
    try {
      const result = await authSignOut(state.user, awsRegion, true);
      
      if (result.success) {
        // Reset local state after successful sign out
        setState({
          isAuth: false,
          doneTokenCheck: true,
          isGettingProfile: false,
          isInvalidUser: false,
          user: {},
        });
        setToken(null);
      }
    } catch (error) {
      console.log('error signing out: ', error);
      createSnack('Sign out failed', 'error', 3000);
    }
  }
  const login = () => {
    console.log('Attempting login with provider:', federatedIdName);
    Auth.federatedSignIn({
      provider: federatedIdName,
    });
  };

  const getMuiTheme = () =>
    createTheme({
      palette: {
        primary: { main: '#1482C5' },
      },
      overrides: {
        MUIDataTableBodyCell: {
          root: {
            padding: '3px 6px',
          },
        },
        MUIDataTableSelectCell: {
          checked: { color: `${state.primaryAppColor} !important` },
        },
      },
    });

  if (!state.doneTokenCheck) {
    return null;
  }
  return (
    <>
      <div className="App">
        <MuiThemeProvider theme={getMuiTheme()}>
          <div style={{ margin: '0 auto' }}>
            <AppBar
              appState={{ ...state, ...appVals, ...logoVals }}
              logout={signOut}
              login={login}
              user={state.user}
              isSigningOut={isSigningOut}
            >
              <div style={{ maxWidth: '1400px', margin: 'auto' }}>
                <div>
                  {state.isAuth ? (
                    <Routes
                      login={login}
                      // userRoles={this.userRoles}
                      createSnack={createSnack}
                      credentials={{
                        ...state,
                        ...appVals,
                        clientModules,
                        login: login,
                      }}
                    />
                  ) : (
                    <LoginPage
                      federatedIdName={federatedIdName}
                      logoVals={logoVals}
                    />
                  )}
                </div>
              </div>
            </AppBar>
          </div>
        </MuiThemeProvider>
      </div>
    </>
  );
}

export default AppCognito;
